import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { MapPin, Trophy, Clock, Share2 } from "lucide-react";
import ShareFindModal from "@/components/ShareFindModal";
import GoldenWinnersCarousel from "@/components/GoldenWinnersCarousel";
import { PrizeTypeBadge, prizeMeta } from "@/components/PrizeDisplay";
import WinnerReactions from "@/components/WinnerReactions";
import { SkeletonGrid } from "@/components/Skeleton";
import OGMeta from "@/components/OGMeta";
import UserBadges from "@/components/UserBadges";
import MysteryInfo from "@/components/MysteryInfo";
import { Link } from "react-router-dom";

export default function WinnersPage() {
  const [winners, setWinners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [city, setCity] = useState("");
  const [shareOf, setShareOf] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const { data } = await api.get("/winners", { params: city ? { city } : {} });
      setWinners(data.winners || []);
      setLoading(false);
    })();
  }, [city]);

  const cities = Array.from(new Set(winners.map((w) => w.city).filter(Boolean)));

  return (
    <div className="py-8 space-y-8">
      <OGMeta
        title="Winners Wall · The Local Loot"
        description="Every finder. Every story. Real rewards. Real proof."
        url={typeof window !== "undefined" ? window.location.href : undefined}
      />
      <GoldenWinnersCarousel />
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="overline">Proof of loot</div>
          <h1 className="font-display uppercase text-5xl md:text-6xl tracking-tight mt-2">
            Winners <span style={{ color: "#D7A62A" }}>wall</span>
          </h1>
          <p className="text-[#F5F2E8]/70 mt-3 max-w-xl">
            Every finder. Every story. Real rewards. Real proof.
          </p>
        </div>
        <Link to="/leaderboard" className="btn-ghost" data-testid="winners-leaderboard-link">
          <Trophy size={14} className="inline mr-1" /> Leaderboard
        </Link>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => setCity("")}
          data-testid="winners-filter-all"
          className={`tag-pill ${city === "" ? "text-[#76B82A] border-[#76B82A]" : ""}`}
        >
          All cities
        </button>
        {cities.map((c) => (
          <button
            key={c}
            onClick={() => setCity(c)}
            data-testid={`winners-filter-${c}`}
            className={`tag-pill ${city === c ? "text-[#76B82A] border-[#76B82A]" : ""}`}
          >
            {c}
          </button>
        ))}
      </div>

      {loading ? (
        <SkeletonGrid count={6} />
      ) : winners.length === 0 ? (
        <div className="loot-card-flat p-12 text-center">
          <Trophy className="mx-auto text-[#F5F2E8]/40" size={40} />
          <p className="mt-3 text-[#F5F2E8]/60">No approved winners yet — be the first.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {winners.map((w) => (
            <div key={w.id} className="loot-card p-5 flex flex-col" data-testid={`winner-card-${w.id}`}>
              <div className="flex items-center justify-between">
                <span className="tag-pill" style={{ color: "#D7A62A", borderColor: "#D7A62A" }}>
                  <Trophy size={10} className="inline mr-1" /> Winner
                </span>
                <span className="font-mono text-xs text-[#F5F2E8]/50 flex items-center gap-1">
                  <MapPin size={12} /> {w.city}
                </span>
              </div>

              {(w.selfie_url || w.photo_url) ? (
                <div
                  className="mt-4 aspect-video bg-[#1a1a1a] border border-[#F5F2E8]/10 overflow-hidden"
                  style={{
                    backgroundImage: `linear-gradient(rgba(118,184,42,0.15), rgba(0,0,0,0.6)), url(${process.env.REACT_APP_BACKEND_URL}${w.selfie_url || w.photo_url})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                />
              ) : (
                <div className="mt-4 aspect-video map-blur flex items-center justify-center">
                  <Trophy size={40} className="text-[#76B82A]" />
                </div>
              )}

              <div className="mt-4 font-display uppercase text-2xl leading-tight">
                {w.username ? (
                  <Link
                    to={`/u/${encodeURIComponent(w.username)}`}
                    className="hover:text-[#76B82A] transition-colors inline-flex items-center gap-2 flex-wrap"
                    data-testid={`winner-username-${w.id}`}
                  >
                    @{w.username}
                    <UserBadges
                      lootLegend={w.user_loot_legend}
                      goodDeeds={w.user_good_deeds_count}
                      igniter={w.user_igniter}
                      badges={w.user_badges}
                      testIdPrefix={`winner-badge-${w.id}`}
                    />
                  </Link>
                ) : (
                  <>@{w.name}</>
                )}
              </div>
              {(() => {
                const pt = w.prize_type || "cash";
                const color = prizeMeta(w).color;
                if (w.is_mystery) {
                  return (
                    <div
                      className="mt-1 font-display uppercase text-xl flex items-center gap-2"
                      style={{ color: "#D7A62A" }}
                      data-testid={`winner-mystery-${w.id}`}
                    >
                      <span>🎁</span> Found Mystery Loot
                      <MysteryInfo size={16} />
                    </div>
                  );
                }
                if (pt === "cash") {
                  return (
                    <div className="mt-1 font-display uppercase text-xl" style={{ color: "#76B82A" }}>
                      {w.hide_amount ? "Found CASH" : `Found $${w.amount}`}
                    </div>
                  );
                }
                return (
                  <div className="mt-1">
                    <div
                      className="font-display uppercase text-xl leading-tight"
                      style={{ color }}
                      data-testid={`winner-prize-${w.id}`}
                    >
                      Found {prizeMeta(w).label}
                    </div>
                    {w.prize_custom_label && (
                      <div className="font-mono text-xs text-[#F5F2E8]/70 mt-0.5">
                        {w.prize_custom_label}
                        {Number(w.amount) > 0 && (
                          <span className="text-[#F5F2E8]/40"> · ~${w.amount} value</span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}
              <div className="mt-2">
                {!w.is_mystery && <PrizeTypeBadge drop={w} />}
              </div>
              <WinnerReactions winnerId={w.id} dataTestId={`reactions-${w.id}`} />
              {w.story && (
                <p className="mt-3 text-sm text-[#F5F2E8]/70 italic">"{w.story}"</p>
              )}
              <div className="mt-4 flex items-center gap-3 text-xs text-[#F5F2E8]/50 font-mono">
                <span className="flex items-center gap-1">
                  <Clock size={12} /> {w.time_to_find_minutes ? `${w.time_to_find_minutes}m` : "—"}
                </span>
                {w.sponsor && <span>via {w.sponsor.business_name}</span>}
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {(w.badges || []).map((b) => (
                  <span key={b} className="tag-pill">{b}</span>
                ))}
              </div>
              <button
                onClick={() => setShareOf(w)}
                className="btn-ghost text-xs mt-4 w-full justify-center inline-flex items-center gap-1"
                data-testid={`winner-share-${w.id}`}
              >
                <Share2 size={14} /> Share your find
              </button>
            </div>
          ))}
        </div>
      )}

      <ShareFindModal
        open={Boolean(shareOf)}
        winner={shareOf || {}}
        onClose={() => setShareOf(null)}
      />
    </div>
  );
}
